import React, { useState } from 'react';
import { useCalculator, CalculatorPokemon } from '../../hooks/useCalculator';
import SimpleAutocomplete from '../Helper/SimpleAutocomplete';
import MovesOverview from './MovesOverview';
import ImportExportModal from './ImportExportModal';
import { getFormeFromPokedexId } from '../../utils/pokemonData';
import { parseShowdownFormat, showdownToCalculatorPokemon, exportBothPokemon } from '../../utils/showdownParser';
import typesData from '../../data/types.json';
import './DamageCalculator.css';

// Extract type names for dropdown
const allTypes = typesData.map(type => type.english);

// Types
interface PokemonPanelProps {
  pokemon: CalculatorPokemon;
  side: 'left' | 'right';
  updatePokemon: (updates: Partial<CalculatorPokemon>) => void;
  selectPokemon: (name: string) => void;
  updateMove: (index: number, moveName: string) => void;
  selectSetCategory: (category: string) => void;
  applySet: (setName: string) => void;
  pokemonOptions: string[];
  moveOptions: string[];
  natureOptions: string[];
  abilityOptions: string[];
  itemOptions: string[];
  setCategories: string[];
  selectedSetCategory: string;
  availablePokemonSets: string[];
  sideConditions: string[];
  toggleSideCondition: (condition: string) => void;
}

const DamageCalculator: React.FC = () => {
  const [isImportExportModalOpen, setIsImportExportModalOpen] = useState(false);

  const {
    leftPokemon,
    rightPokemon,
    battleConditions,
    allPokemon,
    allMoves,
    allNatures,
    allAbilities,
    allItems,
    allSetCategories,
    leftSetCategory,
    rightSetCategory,
    leftAvailablePokemonSets,
    rightAvailablePokemonSets,
    updateLeftPokemon,
    updateRightPokemon,
    selectLeftPokemon,
    selectRightPokemon,
    updateLeftMove,
    updateRightMove,
    applySetToPokemon,
    selectLeftSetCategory,
    selectRightSetCategory,
    toggleWeather,
    toggleTerrain,
    toggleGlobalCondition,
    toggleLeftCondition,
    toggleRightCondition,
    refreshSetCategories
  } = useCalculator();

  // Handle team saved - refresh custom sets
  const handleTeamSaved = () => {
    refreshSetCategories();
  };

  // Handle importing Pokemon from Showdown format
  const handleImport = (text: string) => {
    try {
      const parsedPokemon = parseShowdownFormat(text);

      if (parsedPokemon.length === 0) {
        alert('No Pokemon found in the imported text');
        return;
      }

      // Apply first Pokemon to left side
      if (parsedPokemon[0]) {
        const leftData = showdownToCalculatorPokemon(parsedPokemon[0]);
        updateLeftPokemon(leftData);
      }

      // Apply second Pokemon to right side if available
      if (parsedPokemon[1]) {
        const rightData = showdownToCalculatorPokemon(parsedPokemon[1]);
        updateRightPokemon(rightData);
      }

      alert(`Successfully imported ${parsedPokemon.length} Pokemon`);
    } catch (error) {
      console.error('Import error:', error);
      alert(`Failed to import Pokemon: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handle exporting Pokemon to Showdown format
  const handleExport = (): string => {
    return exportBothPokemon(leftPokemon, rightPokemon);
  };

  // Don't render until data is loaded
  if (allPokemon.length === 0 || allMoves.length === 0 || allNatures.length === 0 ||
      allAbilities.length === 0 || allItems.length === 0 || allSetCategories.length === 0) {
    return (
      <div className="damage-calculator">
        <div className="loading-message">Loading Pokémon data...</div>
      </div>
    );
  }

  const pokemonOptions = allPokemon.map(p => p.name);
  const moveOptions = allMoves.map(m => m.name);
  const natureOptions = allNatures.map(n => n.name);

  // Debug log to check options
  console.log('Options available:', {
    pokemonCount: pokemonOptions.length,
    movesCount: moveOptions.length,
    naturesCount: natureOptions.length,
    abilitiesCount: allAbilities.length,
    itemsCount: allItems.length,
    setCategoriesCount: allSetCategories.length,
    selectedCategory: `L:${leftSetCategory}, R:${rightSetCategory}`,
    availablePokemonSetsCount: `L:${leftAvailablePokemonSets.length}, R:${rightAvailablePokemonSets.length}`,
    firstFewPokemon: pokemonOptions.slice(0, 5),
    firstFewMoves: moveOptions.slice(0, 5),
    firstFewAbilities: allAbilities.slice(0, 5),
    firstFewItems: allItems.slice(0, 5),
    firstFewAvailablePokemonSets: leftAvailablePokemonSets.slice(0, 5)
  });

  const statNames = [
    { key: 'hp', label: 'HP' },
    { key: 'attack', label: 'Attack' },
    { key: 'defense', label: 'Defense' },
    { key: 'sp_attack', label: 'Sp. Atk' },
    { key: 'sp_defense', label: 'Sp. Def' },
    { key: 'speed', label: 'Speed' }
  ] as const;

  const PokemonPanel: React.FC<PokemonPanelProps> = ({
    pokemon,
    side,
    updatePokemon,
    selectPokemon,
    updateMove,
    selectSetCategory,
    applySet,
    pokemonOptions,
    moveOptions,
    natureOptions,
    abilityOptions,
    itemOptions,
    setCategories,
    selectedSetCategory,
    availablePokemonSets,
    sideConditions,
    toggleSideCondition
  }) => {
    return (
      <div className={`pokemon-panel ${side}`}>
        {/* Sets & Category Row — sets first, category filter on the right */}
        <div className="input-row sets-category-row">
          <div className="input-group">
            <label>Pokemon Sets:</label>
            <SimpleAutocomplete
              value={pokemon.selectedSetName || ''}
              onChange={(setName: string) => applySet(setName)}
              options={availablePokemonSets}
              placeholder="Search sets..."
            />
          </div>
          <div className="input-group">
            <label>Filter:</label>
            <SimpleAutocomplete
              value={selectedSetCategory}
              onChange={(category: string) => selectSetCategory(category)}
              options={setCategories}
              placeholder="All"
            />
          </div>
        </div>

        {/* Types Row - 3 editable type dropdowns */}
        <div className="input-row horizontal-layout">
          <label className="row-label">Type:</label>
          <div className="horizontal-input types-grid">
            <select
              value={pokemon.types?.[0] || pokemon.pokemon?.types?.[0] || ''}
              onChange={(e) => updatePokemon({
                types: [e.target.value, pokemon.types?.[1] || pokemon.pokemon?.types?.[1] || '', pokemon.types?.[2] || '']
              })}
              className="type-select"
            >
              <option value="">None</option>
              {allTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <select
              value={pokemon.types?.[1] || pokemon.pokemon?.types?.[1] || ''}
              onChange={(e) => updatePokemon({
                types: [pokemon.types?.[0] || pokemon.pokemon?.types?.[0] || '', e.target.value, pokemon.types?.[2] || '']
              })}
              className="type-select"
            >
              <option value="">None</option>
              {allTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <select
              value={pokemon.types?.[2] || ''}
              onChange={(e) => updatePokemon({
                types: [pokemon.types?.[0] || pokemon.pokemon?.types?.[0] || '', pokemon.types?.[1] || pokemon.pokemon?.types?.[1] || '', e.target.value]
              })}
              className="type-select"
            >
              <option value="">None</option>
              {allTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Forme Row - Now read-only, derived from Pokemon data */}
        <div className="input-row horizontal-layout">
          <label className="row-label">Forme:</label>
          <div className="horizontal-input readonly-field">
            {pokemon.pokemon ? getFormeFromPokedexId(pokemon.pokemon.id) : 'None'}
          </div>
        </div>

        {/* Gender Row */}
        <div className="input-row horizontal-layout">
          <label className="row-label">Gender:</label>
          <SimpleAutocomplete
            value={pokemon.gender || ''}
            onChange={(gender: string) => updatePokemon({ gender: gender })}
            options={['Male', 'Female', 'Genderless']}
            placeholder="Gender"
            className="horizontal-input"
          />
        </div>

        {/* Level Row */}
        <div className="input-row horizontal-layout">
          <label className="row-label">Level:</label>
          <input
            type="number"
            value={pokemon.level}
            onChange={(e) => updatePokemon({ level: parseInt(e.target.value) || 100 })}
            min="1"
            max="100"
            className="level-input horizontal-input"
          />
        </div>

        {/* Stats Section */}
        <div className="stats-section">
          <div className="stats-header-row">
            <span className="stat-header">Stat</span>
            <span className="stat-header">IV</span>
            <span className="stat-header">EV</span>
            <span className="stat-header">Stage</span>
            <span className="stat-header">Total</span>
          </div>

          {statNames.map(({ key, label }) => (
            <div key={key} className="stat-row">
              <span className="stat-label">{label}</span>
              <input
                type="number"
                value={pokemon.ivs[key]}
                onChange={(e) => updatePokemon({
                  ivs: { ...pokemon.ivs, [key]: parseInt(e.target.value) || 0 }
                })}
                min="0"
                max="31"
                className="iv-input"
              />
              <input
                type="number"
                value={pokemon.evs[key]}
                onChange={(e) => updatePokemon({
                  evs: { ...pokemon.evs, [key]: parseInt(e.target.value) || 0 }
                })}
                min="0"
                max="252"
                className="ev-input"
              />
              <select
                value={pokemon.stages[key] || 0}
                onChange={(e) => updatePokemon({
                  stages: { ...pokemon.stages, [key]: parseInt(e.target.value) }
                })}
                className="stage-select"
              >
                <option value="6">+6</option>
                <option value="5">+5</option>
                <option value="4">+4</option>
                <option value="3">+3</option>
                <option value="2">+2</option>
                <option value="1">+1</option>
                <option value="0">0</option>
                <option value="-1">-1</option>
                <option value="-2">-2</option>
                <option value="-3">-3</option>
                <option value="-4">-4</option>
                <option value="-5">-5</option>
                <option value="-6">-6</option>
              </select>
              <span className="calculated-stat">
                {pokemon.calculatedStats?.[key] || 0}
              </span>
            </div>
          ))}
        </div>

        {/* Nature + Ability */}
        <div className="input-row two-col-row">
          <div className="input-group">
            <label>Nature:</label>
            <SimpleAutocomplete
              value={pokemon.nature?.name || ''}
              onChange={(natureName: string) => {
                const nature = allNatures.find(n => n.name === natureName);
                updatePokemon({ nature });
              }}
              options={natureOptions}
              placeholder="Select nature..."
            />
          </div>
          <div className="input-group">
            <label>Ability:</label>
            <SimpleAutocomplete
              value={pokemon.ability}
              onChange={(ability: string) => updatePokemon({ ability })}
              options={abilityOptions}
              placeholder="Select ability..."
            />
          </div>
        </div>

        {/* Item + Status */}
        <div className="input-row two-col-row">
          <div className="input-group">
            <label>Item:</label>
            <SimpleAutocomplete
              value={pokemon.item}
              onChange={(item: string) => updatePokemon({ item })}
              options={itemOptions}
              placeholder="Select item..."
            />
          </div>
          <div className="input-group">
            <label>Status:</label>
            <SimpleAutocomplete
              value={pokemon.status}
              onChange={(status: string) => updatePokemon({ status })}
              options={['Healthy', 'Burned', 'Paralyzed', 'Poisoned', 'Badly Poisoned', 'Asleep', 'Frozen']}
              placeholder="Select status..."
            />
          </div>
        </div>

        {/* Current HP Section */}
        <div className="current-hp-section">
          <label>Current HP:</label>
          <div className="hp-display">
            <input
              type="number"
              value={pokemon.currentHP?.current || pokemon.calculatedStats?.hp || 100}
              onChange={(e) => {
                const current = parseInt(e.target.value) || 0;
                const total = pokemon.calculatedStats?.hp || 100;
                updatePokemon({
                  currentHP: {
                    current: Math.min(current, total),
                    total,
                    percentage: Math.round((Math.min(current, total) / total) * 100)
                  }
                });
              }}
              min="0"
              max={pokemon.calculatedStats?.hp || 100}
              className="hp-input"
            />
            <span>/ {pokemon.calculatedStats?.hp || 100} ({Math.round(((pokemon.currentHP?.current || pokemon.calculatedStats?.hp || 100) / (pokemon.calculatedStats?.hp || 100)) * 100)}%)</span>
          </div>
        </div>

        {/* Moves Section */}
        <div className="moves-section">
          <div className="moves-table">
            <div className="moves-header">
              <span>Move</span>
              <span>Power</span>
              <span>Type</span>
              <span>Category</span>
              <span>Crit</span>
              <span>Z</span>
            </div>
            {pokemon.moves.map((move, index) => (
              <div key={index} className="move-row">
                <SimpleAutocomplete
                  value={move.name}
                  onChange={(moveName: string) => updateMove(index, moveName)}
                  options={moveOptions}
                  placeholder="Select move..."
                  className="move-select"
                />
                <input
                  type="number"
                  value={move.power || ''}
                  onChange={(e) => {
                    const newMoves = [...pokemon.moves];
                    newMoves[index] = { ...move, power: parseInt(e.target.value) || 0 };
                    updatePokemon({ moves: newMoves });
                  }}
                  min="0"
                  max="250"
                  className="power-input"
                  placeholder="0"
                />
                <select
                  value={move.type}
                  onChange={(e) => {
                    const newMoves = [...pokemon.moves];
                    newMoves[index] = { ...move, type: e.target.value };
                    updatePokemon({ moves: newMoves });
                  }}
                  className="type-select"
                >
                  <option value="Normal">Normal</option>
                  <option value="Fire">Fire</option>
                  <option value="Water">Water</option>
                  <option value="Electric">Electric</option>
                  <option value="Grass">Grass</option>
                  <option value="Ice">Ice</option>
                  <option value="Fighting">Fighting</option>
                  <option value="Poison">Poison</option>
                  <option value="Ground">Ground</option>
                  <option value="Flying">Flying</option>
                  <option value="Psychic">Psychic</option>
                  <option value="Bug">Bug</option>
                  <option value="Rock">Rock</option>
                  <option value="Ghost">Ghost</option>
                  <option value="Dragon">Dragon</option>
                  <option value="Dark">Dark</option>
                  <option value="Steel">Steel</option>
                  <option value="Fairy">Fairy</option>
                </select>
                <select
                  value={move.category}
                  onChange={(e) => {
                    const newMoves = [...pokemon.moves];
                    newMoves[index] = { ...move, category: e.target.value as 'Physical' | 'Special' | 'Status' };
                    updatePokemon({ moves: newMoves });
                  }}
                  className="category-select"
                >
                  <option value="Physical">Physical</option>
                  <option value="Special">Special</option>
                  <option value="Status">Status</option>
                </select>
                <button
                  type="button"
                  className={`toggle-button ${move.crit ? 'active' : ''}`}
                  onClick={() => {
                    const newMoves = [...pokemon.moves];
                    newMoves[index] = { ...move, crit: !move.crit };
                    updatePokemon({ moves: newMoves });
                  }}
                >
                  {move.crit ? '✓' : '✗'}
                </button>
                <button
                  type="button"
                  className={`toggle-button ${move.zmove ? 'active' : ''}`}
                  onClick={() => {
                    const newMoves = [...pokemon.moves];
                    newMoves[index] = { ...move, zmove: !move.zmove };
                    updatePokemon({ moves: newMoves });
                  }}
                >
                  {move.zmove ? '✓' : '✗'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Side Conditions Section */}
        <div className="side-conditions-section">
          <div className="side-conditions-header">Field Effects</div>
          <div className="side-conditions-grid">
            {['Stealth Rock', 'Spikes', 'Light Screen', 'Reflect', 'Protect',
              'Leech Seed', 'Salt Cure', 'Foresight', 'Helping Hand', 'Tailwind',
              'Flower Gift', 'Power Trick', 'Steely Spirit', 'Friend Guard',
              'Aurora Veil', 'Battery', 'Power Spot', 'Switching Out'].map(condition => {
              const isSpikes = condition === 'Spikes';
              const spikesLevel = isSpikes ? sideConditions.find(c => c.startsWith('Spikes')) : undefined;
              const isActive = isSpikes ? !!spikesLevel : sideConditions.includes(condition);
              const label = spikesLevel ? `Spikes ×${spikesLevel.split(' ')[1]}` : condition;
              return (
                <button
                  key={condition}
                  type="button"
                  className={`condition-button ${isActive ? 'active' : ''}`}
                  onClick={() => toggleSideCondition(isSpikes ? 'Spikes' : condition)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="damage-calculator">
      <div className="calculator-header">
        <MovesOverview
          leftPokemon={leftPokemon}
          rightPokemon={rightPokemon}
          battleConditions={battleConditions}
        />
      </div>

      {/* ── Battle Conditions bar (above the pokemon panels) ── */}
      <div className="battle-conditions-bar">
        <div className="conditions-section">
          <h4>Weather</h4>
          <div className="conditions-grid">
            {([
              { value: 'Clear', label: 'None' },
              { value: 'Sun', label: 'Sun' },
              { value: 'Rain', label: 'Rain' },
              { value: 'Sandstorm', label: 'Sand' },
              { value: 'Hail', label: 'Hail' },
              { value: 'Snow', label: 'Snow' },
              { value: 'Harsh Sun', label: 'Harsh Sunshine' },
              { value: 'Heavy Rain', label: 'Heavy Rain' },
              { value: 'Strong Winds', label: 'Strong Winds' },
            ] as const).map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={`condition-button ${battleConditions.weather.includes(value) ? 'active' : ''}`}
                onClick={() => toggleWeather(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="conditions-section">
          <h4>Terrain</h4>
          <div className="conditions-grid">
            {['Electric', 'Grassy', 'Misty', 'Psychic'].map(terrain => (
              <button
                key={terrain}
                type="button"
                className={`condition-button ${battleConditions.terrain.includes(terrain) ? 'active' : ''}`}
                onClick={() => toggleTerrain(terrain)}
              >
                {terrain}
              </button>
            ))}
          </div>
        </div>

        <div className="conditions-section">
          <h4>Other</h4>
          <div className="conditions-grid">
            {['Magic Room', 'Wonder Room', 'Gravity'].map(condition => (
              <button
                key={condition}
                type="button"
                className={`condition-button ${battleConditions.globalConditions.includes(condition) ? 'active' : ''}`}
                onClick={() => toggleGlobalCondition(condition)}
              >
                {condition}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Pokemon panels (two equal columns) ── */}
      <div className="calculator-content">
        <div className="pokemon-section">
          <PokemonPanel
            pokemon={leftPokemon}
            side="left"
            updatePokemon={updateLeftPokemon}
            selectPokemon={selectLeftPokemon}
            updateMove={updateLeftMove}
            selectSetCategory={selectLeftSetCategory}
            applySet={(setName: string) => applySetToPokemon(setName, 'left')}
            pokemonOptions={leftAvailablePokemonSets}
            moveOptions={moveOptions}
            natureOptions={natureOptions}
            abilityOptions={allAbilities}
            itemOptions={allItems}
            setCategories={allSetCategories}
            selectedSetCategory={leftSetCategory}
            availablePokemonSets={leftAvailablePokemonSets}
            sideConditions={battleConditions.leftConditions}
            toggleSideCondition={toggleLeftCondition}
          />
        </div>

        <div className="pokemon-section">
          <PokemonPanel
            pokemon={rightPokemon}
            side="right"
            updatePokemon={updateRightPokemon}
            selectPokemon={selectRightPokemon}
            updateMove={updateRightMove}
            selectSetCategory={selectRightSetCategory}
            applySet={(setName: string) => applySetToPokemon(setName, 'right')}
            pokemonOptions={rightAvailablePokemonSets}
            moveOptions={moveOptions}
            natureOptions={natureOptions}
            abilityOptions={allAbilities}
            itemOptions={allItems}
            setCategories={allSetCategories}
            selectedSetCategory={rightSetCategory}
            availablePokemonSets={rightAvailablePokemonSets}
            sideConditions={battleConditions.rightConditions}
            toggleSideCondition={toggleRightCondition}
          />
        </div>
      </div>

      <div className="calculator-footer">
        <button
          className="import-button"
          onClick={() => setIsImportExportModalOpen(true)}
        >
          Import/Export
        </button>
      </div>

      <ImportExportModal
        isOpen={isImportExportModalOpen}
        onClose={() => setIsImportExportModalOpen(false)}
        onImport={handleImport}
        onExport={handleExport}
        onTeamSaved={handleTeamSaved}
      />
    </div>
  );
};

export default DamageCalculator;
